
import * as THREE from "../../libs/three.js/build/three.module.js";
import {Annotation} from "../Annotation.js";
import {CameraMode} from "../defines.js";
import {View} from "./View.js";
import {Utils} from "../utils.js";
import {EventDispatcher} from "../EventDispatcher.js";

/**
 * 表示三维场景，包含点云、测量工具、剖面、体积、剪切体等。
 * @class
 */
export class Scene extends EventDispatcher{

	 /**
     * 构造一个新的三维场景对象。
	 * @constructor
     */
	constructor(){
		super();
		/**
		 * 注释管理器，用于管理场景中的注释。
		 * @type {Annotation}
		 */
		this.annotations = new Annotation();
		/**
		 * 主场景对象，用于渲染大部分3D对象。
		 * @type {THREE.Scene}
		 */
		this.scene = new THREE.Scene();
		/**
		 * 背景场景对象，用于渲染背景元素。
		 * @type {THREE.Scene}
		 */
		this.sceneBG = new THREE.Scene();
		/**
		 * 专用于点云的场景对象。
		 * @type {THREE.Scene}
		 */
		this.scenePointCloud = new THREE.Scene();
		/**
		 * 透视相机，用于3D场景的透视视图。
		 * @type {THREE.PerspectiveCamera}
		 */
		this.cameraP = new THREE.PerspectiveCamera(this.fov, 1, 0.1, 1000*1000);
		/**
		 * 正交相机，用于3D场景的正交视图。
		 * @type {THREE.OrthographicCamera}
		 */
		this.cameraO = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.1, 1000*1000);
		/**
		 * VR相机。
		 * @type {THREE.PerspectiveCamera}
		 */
		this.cameraVR = new THREE.PerspectiveCamera();
		/**
		 * 背景相机。
		 * @type {THREE.Camera}
		 */
		this.cameraBG = new THREE.Camera();
		/**
		 * 屏幕空间相机。
		 * @type {THREE.OrthographicCamera}
		 */
		this.cameraScreenSpace = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.1, 10);
		/**
		 * 当前激活的相机模式。
		 * @type {CameraMode}
		 */
		this.cameraMode = CameraMode.PERSPECTIVE;
		/**
		 * 覆盖相机，如果设置，将优先使用此相机进行渲染。
		 * @type {THREE.Camera|null}
		 */
		this.overrideCamera = null;
		/**
		 * 场景中的点云对象数组。
		 * @type {Array}
		 */
		this.pointclouds = [];
		/**
		 * 场景中的测量对象数组。
		 * @type {Array}
		 */
		this.measurements = [];
		/**
		 * 场景中的剖面对象数组。
		 * @type {Array}
		 */
		this.profiles = [];
		/**
		 * 场景中的体积对象数组。
		 * @type {Array}
		 */
		this.volumes = [];
		/**
		 * 场景中的多边形剪切体积对象数组。
		 * @type {Array}
		 */
		this.polygonClipVolumes = [];
		/**
		 * 场景中的相机动画对象数组。
		 * @type {Array}
		 */
		this.cameraAnimations = [];
		/**
		 * 场景中的定向图像对象数组。
		 * @type {Array}
		 */
		this.orientedImages = [];
		/**
		 * 场景中的360度图像对象数组。
		 * @type {Array}
		 */
		this.images360 = [];
		/**
		 * 场景中的地理数据包对象数组。
		 * @type {Array}
		 */
		this.geopackages = [];
		/**
		 * 挂载到主场景的外部渲染对象（如 3DGS）。
		 * @type {Array<THREE.Object3D>}
		 */
		this.externalRenderables = [];
		/**
		 * 第一人称控制器。
		 * @type {FirstPersonControls|null}
		 */
		this.fpControls = null;
		/**
		 * 轨道控制器。
		 * @type {OrbitControls|null}
		 */
		this.orbitControls = null;
		/**
		 * 地球控制器。
		 * @type {EarthControls|null}
		 */
		this.earthControls = null;
		/**
		 * 地理控制器。
		 * @type {GeoControls|null}
		 */
		this.geoControls = null;
		/**
		 * 设备方向控制器。
		 * @type {DeviceOrientationControls|null}
		 */
		this.deviceControls = null;
		/**
		 * 输入处理器，用于管理用户输入。
		 * @type {InputHandler|null}
		 */
		this.inputHandler = null;
		/**
		 * 视图对象，包含有关场景视图状态的信息。
		 * @type {View}
		 */
		this.view = new View();
		/**
		 * 方向光源。
		 * @type {THREE.DirectionalLight|null}
		 */
		this.directionalLight = null;

		this.initialize();
	}

	/**
     * 估计给定位置的高度。
     * @param {THREE.Vector3} position - 要估计高度的位置。
     * @returns {number|null} 估计的高度，如果无法估计则为 null。
     */
	estimateHeightAt (position) {
		let height = null;
		let fromSpacing = Infinity;

		for (let pointcloud of this.pointclouds) {
			if (pointcloud.root.geometryNode === undefined) {
				continue;
			}

			let pHeight = null;
			let pFromSpacing = Infinity;

			let lpos = position.clone().sub(pointcloud.position);
			lpos.z = 0;
			let ray = new THREE.Ray(lpos, new THREE.Vector3(0, 0, 1));

			let stack = [pointcloud.root];
			while (stack.length > 0) {
				let node = stack.pop();
				let box = node.getBoundingBox();

				let inside = ray.intersectBox(box);

				if (!inside) {
					continue;
				}

				let h = node.geometryNode.mean.z +
					pointcloud.position.z +
					node.geometryNode.boundingBox.min.z;

				if (node.geometryNode.spacing <= pFromSpacing) {
					pHeight = h;
					pFromSpacing = node.geometryNode.spacing;
				}

				for (let index of Object.keys(node.children)) {
					let child = node.children[index];
					if (child.geometryNode) {
						stack.push(node.children[index]);
					}
				}
			}

			if (height === null || pFromSpacing < fromSpacing) {
				height = pHeight;
				fromSpacing = pFromSpacing;
			}
		}

		return height;
	}
	/**
     * 获取指定点云的包围盒。
     * @param {Array} pointclouds - 点云数组，默认为场景中的所有点云。
     * @returns {THREE.Box3} 包围盒。
     */
	getBoundingBox(pointclouds = this.pointclouds){
		let box = new THREE.Box3();

		this.scenePointCloud.updateMatrixWorld(true);
		this.referenceFrame.updateMatrixWorld(true);

		for (let pointcloud of pointclouds) {
			pointcloud.updateMatrixWorld(true);

			if (pointcloud.pcoGeometry.tightBoundingBox) {
				let pointcloudBox = pointcloud.pcoGeometry.tightBoundingBox;
				box.union(pointcloudBox);
			} else {
				let pointcloudBox = pointcloud.boundingBox
				let boxWorld = Utils.computeTransformedBoundingBox(pointcloudBox, pointcloud.matrixWorld);
				box.union(boxWorld);
			}
		}

		return box;
	}
	/**
	 * 向场景中添加点云。
	 * @param {PointCloud} pointcloud - 要添加的点云。
	 */
	addPointCloud (pointcloud) {
		this.pointclouds.push(pointcloud);
		this.scenePointCloud.add(pointcloud);

		this.dispatchEvent({
			type: 'pointcloud_added',
			pointcloud: pointcloud
		});
	}
	/**
	 * 判断外部对象类型。
	 * @param {THREE.Object3D} object - 外部对象。
	 * @returns {string} 类型标识。
	 */
	getExternalRenderableType(object){
		if(!object){
			return "unknown";
		}

		if(object.userData && typeof object.userData.externalType === "string"){
			return object.userData.externalType.toLowerCase();
		}

		const sourceFormat = object.userData ? object.userData.sourceFormat : null;
		if(typeof sourceFormat === "string"){
			const format = sourceFormat.toLowerCase();
			if(format === "gltf" || format === "glb"){
				return "glb";
			}
			if(format === "fbx"){
				return "fbx";
			}
		}

		const ctorName = object.constructor && object.constructor.name ? object.constructor.name.toLowerCase() : "";
		if(ctorName.includes("renderer")){
			return "renderer";
		}
		if(ctorName.includes("splat") || ctorName.includes("spark")){
			return "3dgs";
		}
		if(object.isSkinnedMesh){
			return "skinned-mesh";
		}
		if(object.isMesh){
			return "mesh";
		}
		if(object.isGroup){
			return "group";
		}
		if(object.isPoints){
			return "points";
		}
		if(object.isLine || object.isLineSegments || object.isLineLoop){
			return "line";
		}

		return "object3d";
	}
	/**
	 * 获取外部对象列表，可按类型筛选。
	 * @param {Object} [filters] - 筛选条件。
	 * @param {string|string[]} [filters.type] - 类型或类型数组。
	 * @returns {THREE.Object3D[]} 外部对象列表。
	 */
	getExternalRenderables(filters = {}){
		const all = [...this.externalRenderables];
		const includeHidden = filters && filters.includeHidden === true;

		let result = all;
		if(!includeHidden){
			result = result.filter(object => object.userData?.showInSidebar !== false);
		}

		if(!filters || filters.type == null){
			return result;
		}

		const typeFilter = Array.isArray(filters.type) ? filters.type : [filters.type];
		const typeSet = new Set(typeFilter.map(type => String(type).toLowerCase()));

		return result.filter(object => typeSet.has(this.getExternalRenderableType(object)));
	}
	/**
	 * 获取按类型分组的外部对象。
	 * @returns {Object<string, THREE.Object3D[]>} 按类型分组结果。
	 */
	getExternalRenderableTypeMap(){
		const grouped = {};
		for(const object of this.externalRenderables){
			const type = this.getExternalRenderableType(object);
			if(!grouped[type]){
				grouped[type] = [];
			}
			grouped[type].push(object);
		}

		return grouped;
	}
	/**
	 * 语义化别名：按类型获取模型列表。
	 * @param {string|string[]} type - 类型或类型数组。
	 * @returns {THREE.Object3D[]} 模型列表。
	 */
	getModelsByType(type){
		return this.getExternalRenderables({type: type});
	}
	/**
	 * 语义化别名：获取按类型分组的模型列表。
	 * @returns {Object<string, THREE.Object3D[]>} 按类型分组结果。
	 */
	getModelTypeMap(){
		return this.getExternalRenderableTypeMap();
	}
	/**
	 * 向主场景添加外部渲染对象（如 3DGS、普通 Mesh、GLB/FBX 的 Group）。
	 * @param {THREE.Object3D} object - 要添加的对象。
	 * @param {Object} [options] - 可选参数。
	 * @param {string} [options.type] - 外部类型覆盖值。
	 * @param {string} [options.name] - 对象名称（仅当 object.name 为空时写入）。
	 * @param {boolean} [options.showInSidebar=true] - 是否显示在侧边栏对象树。
	 */
	addExternalRenderable(object, options = {}){
		if(!object || (typeof object !== "object" && typeof object !== "function")){
			console.warn("addExternalRenderable expects an object-like renderable");
			return false;
		}
		if(this.externalRenderables.includes(object)){
			return false;
		}

		const externalType = options.type ? String(options.type).toLowerCase() : this.getExternalRenderableType(object);
		object.userData = object.userData || {};
		object.userData.externalType = externalType;
		object.userData.showInSidebar = options.showInSidebar !== false;
		if(options.name && !object.name){
			object.name = options.name;
		}

		try{
			this.scene.add(object);
		}catch(error){
			console.warn("addExternalRenderable failed to add object to scene", error);
			return false;
		}

		this.externalRenderables.push(object);

		this.dispatchEvent({
			type: "external_renderable_added",
			scene: this,
			object: object,
			externalType: externalType
		});

		return true;
	}
	/**
	 * 从主场景移除外部渲染对象。
	 * @param {THREE.Object3D} object - 要移除的对象。
	 */
	removeExternalRenderable(object){
		const index = this.externalRenderables.indexOf(object);
		if(index > -1){
			const externalType = this.getExternalRenderableType(object);
			this.externalRenderables.splice(index, 1);
			this.scene.remove(object);

			this.dispatchEvent({
				type: "external_renderable_removed",
				scene: this,
				object: object,
				externalType: externalType
			});

			return true;
		}

		return false;
	}
	/**
	 * 语义化别名：添加普通模型（Mesh/Group/GLB/FBX root）。
	 * @param {THREE.Object3D} model - 模型根节点。
	 * @param {Object} [options] - 可选参数（同 addExternalRenderable）。
	 * @returns {boolean} 是否成功添加。
	 */
	addModel(model, options = {}){
		return this.addExternalRenderable(model, options);
	}
	/**
	 * 语义化别名：移除普通模型（Mesh/Group/GLB/FBX root）。
	 * @param {THREE.Object3D} model - 模型根节点。
	 * @returns {boolean} 是否成功移除。
	 */
	removeModel(model){
		return this.removeExternalRenderable(model);
	}
	/**
	 * 批量添加模型/外部对象。
	 * @param {Array<THREE.Object3D>} objects - 要添加的对象列表。
	 */
	addExternalRenderables(objects = []){
		for(const object of objects){
			this.addExternalRenderable(object);
		}
	}
	/**
	 * 批量移除模型/外部对象。
	 * @param {Array<THREE.Object3D>} objects - 要移除的对象列表。
	 */
	removeExternalRenderables(objects = []){
		for(const object of objects){
			this.removeExternalRenderable(object);
		}
	}
	/**
     * 添加体积到场景。
     * @param {Volume} volume - 要添加的体积。
     */
	addVolume (volume) {
		this.volumes.push(volume);
		this.dispatchEvent({
			'type': 'volume_added',
			'scene': this,
			'volume': volume
		});
	}
	/**
	 * 向场景中添加定向影像。
	 * @param {OrientedImages} images - 要添加的定向影像集。
	 */
	addOrientedImages(images){
		this.orientedImages.push(images);
		this.scene.add(images.node);

		this.dispatchEvent({
			'type': 'oriented_images_added',
			'scene': this,
			'images': images
		});
	};
	/**
	 * 从场景中移除定向影像。
	 * @param {OrientedImages} images - 要移除的定向影像集。
	 */
	removeOrientedImages(images){
		let index = this.orientedImages.indexOf(images);
		if (index > -1) {
			this.orientedImages.splice(index, 1);

			this.dispatchEvent({
				'type': 'oriented_images_removed',
				'scene': this,
				'images': images
			});
		}
	};
	/**
	 * 向场景中添加 360 度影像。
	 * @param {Images360} images - 要添加的 360 度影像集。
	 */
	add360Images(images){
		this.images360.push(images);
		this.scene.add(images.node);

		this.dispatchEvent({
			'type': '360_images_added',
			'scene': this,
			'images': images
		});
	}
	/**
	 * 从场景中移除 360 度影像。
	 * @param {Images360} images - 要移除的 360 度影像集。
	 */
	remove360Images(images){
		let index = this.images360.indexOf(images);
		if (index > -1) {
			this.images360.splice(index, 1);

			this.dispatchEvent({
				'type': '360_images_removed',
				'scene': this,
				'images': images
			});
		}
	}
	/**
	 * 向场景中添加 GeoPackage 数据。
	 * @param {GeoPackage} geopackage - 要添加的 GeoPackage 数据。
	 */
	addGeopackage(geopackage){
		this.geopackages.push(geopackage);
		this.scene.add(geopackage.node);

		this.dispatchEvent({
			'type': 'geopackage_added',
			'scene': this,
			'geopackage': geopackage
		});
	};

	/**
	 * 从场景中移除 GeoPackage 数据。
	 * @param {GeoPackage} geopackage - 要移除的 GeoPackage 数据。
	 */
	removeGeopackage(geopackage){
		let index = this.geopackages.indexOf(geopackage);
		if (index > -1) {
			this.geopackages.splice(index, 1);

			this.dispatchEvent({
				'type': 'geopackage_removed',
				'scene': this,
				'geopackage': geopackage
			});
		}
	};

	/**
	 * 从场景中移除体积。
	 * @param {Volume} volume - 要移除的体积。
	 */
	removeVolume (volume) {
		let index = this.volumes.indexOf(volume);
		if (index > -1) {
			this.volumes.splice(index, 1);

			this.dispatchEvent({
				'type': 'volume_removed',
				'scene': this,
				'volume': volume
			});
		}
	};
	/**
	 * 向场景中添加摄像机动画。
	 * @param {CameraAnimation} animation - 要添加的摄像机动画。
	 */
	addCameraAnimation(animation) {
		this.cameraAnimations.push(animation);
		this.dispatchEvent({
			'type': 'camera_animation_added',
			'scene': this,
			'animation': animation
		});
	};

	/**
	 * 从场景中移除摄像机动画。
	 * @param {CameraAnimation} animation - 要移除的摄像机动画。
	 */
	removeCameraAnimation(animation){
		let index = this.cameraAnimations.indexOf(volume);
		if (index > -1) {
			this.cameraAnimations.splice(index, 1);

			this.dispatchEvent({
				'type': 'camera_animation_removed',
				'scene': this,
				'animation': animation
			});
		}
	};
	/**
	 * 向场景中添加多边形剪切体积。
	 * @param {PolygonClipVolume} volume - 要添加的多边形剪切体积。
	 */
	addPolygonClipVolume(volume){
		this.polygonClipVolumes.push(volume);
		this.dispatchEvent({
			"type": "polygon_clip_volume_added",
			"scene": this,
			"volume": volume
		});
	};
	/**
	 * 从场景中移除多边形剪切体积。
	 * @param {PolygonClipVolume} volume - 要移除的多边形剪切体积。
	 */
	removePolygonClipVolume(volume){
		let index = this.polygonClipVolumes.indexOf(volume);
		if (index > -1) {
			this.polygonClipVolumes.splice(index, 1);
			this.dispatchEvent({
				"type": "polygon_clip_volume_removed",
				"scene": this,
				"volume": volume
			});
		}
	};
	/**
	 * 向场景中添加测量工具。
	 * @param {Measurement} measurement - 要添加的测量工具。
	 */	
	addMeasurement(measurement){
		measurement.lengthUnit = this.lengthUnit;
		measurement.lengthUnitDisplay = this.lengthUnitDisplay;
		this.measurements.push(measurement);
		this.dispatchEvent({
			'type': 'measurement_added',
			'scene': this,
			'measurement': measurement
		});
	};
	/**
	 * 从场景中移除测量工具。
	 * @param {Measurement} measurement - 要移除的测量工具。
	 */
	removeMeasurement (measurement) {
		let index = this.measurements.indexOf(measurement);
		if (index > -1) {
			this.measurements.splice(index, 1);
			this.dispatchEvent({
				'type': 'measurement_removed',
				'scene': this,
				'measurement': measurement
			});
		}
	}
	/**
	 * 向场景中添加多边形剪切体积(挖填方)。
	 * @param {PolygonClipVolume} volume - 要添加的多边形剪切体积。
	 */
	addPolygonCutFillVolume(volume){
		this.polygonClipVolumes.push(volume);
		this.dispatchEvent({
			"type": "polygon_cut_fill_volume_added",
			"scene": this,
			"volume": volume
		});
	};
	/**
	 * 从场景中移除多边形剪切体积(挖填方)。
	 * @param {PolygonClipVolume} volume - 要移除的多边形剪切体积。
	 */
	removePolygonCutFillVolume(volume){
		let index = this.polygonClipVolumes.indexOf(volume);
		if (index > -1) {
			this.polygonClipVolumes.splice(index, 1);
			this.dispatchEvent({
				"type": "polygon_cut_fill_volume_removed",
				"scene": this,
				"volume": volume
			});
		}
	};
	/**
	 * 向场景中添加剖面。
	 * @param {Profile} profile - 要添加的剖面。
	 */
	addProfile (profile) {
		this.profiles.push(profile);
		this.dispatchEvent({
			'type': 'profile_added',
			'scene': this,
			'profile': profile
		});
	}
	/**
	 * 从场景中移除剖面。
	 * @param {Profile} profile - 要移除的剖面。
	 */
	removeProfile (profile) {
		let index = this.profiles.indexOf(profile);
		if (index > -1) {
			this.profiles.splice(index, 1);
			this.dispatchEvent({
				'type': 'profile_removed',
				'scene': this,
				'profile': profile
			});
		}
	}
	/**
	 * 移除场景中所有的测量工具、剖面和体积。
	 */
	removeAllMeasurements () {
		while (this.measurements.length > 0) {
			this.removeMeasurement(this.measurements[0]);
		}

		while (this.profiles.length > 0) {
			this.removeProfile(this.profiles[0]);
		}

		while (this.volumes.length > 0) {
			this.removeVolume(this.volumes[0]);
		}
	}
	/**
	 * 移除场景中所有的剪切体积。
	 */
	removeAllClipVolumes(){
		let clipVolumes = this.volumes.filter(volume => volume.clip === true);
		for(let clipVolume of clipVolumes){
			this.removeVolume(clipVolume);
		}

		while(this.polygonClipVolumes.length > 0){
			this.removePolygonClipVolume(this.polygonClipVolumes[0]);
		}
	}
    /**
     * 获取场景中当前激活的摄像机。
     * @returns {THREE.Camera} 激活的摄像机。
     */
	getActiveCamera() {

		if(this.overrideCamera){
			return this.overrideCamera;
		}

		if(this.cameraMode === CameraMode.PERSPECTIVE){
			return this.cameraP;
		}else if(this.cameraMode === CameraMode.ORTHOGRAPHIC){
			return this.cameraO;
		}else if(this.cameraMode === CameraMode.VR){
			return this.cameraVR;
		}

		return null;
	}
    /**
     * 初始化场景，设置参考帧、灯光等。
     */	
	initialize(){
		
		this.referenceFrame = new THREE.Object3D();
		this.referenceFrame.matrixAutoUpdate = false;
		this.scenePointCloud.add(this.referenceFrame);

		this.cameraP.up.set(0, 0, 1);
		this.cameraP.position.set(1000, 1000, 1000);
		this.cameraO.up.set(0, 0, 1);
		this.cameraO.position.set(1000, 1000, 1000);
		//this.camera.rotation.y = -Math.PI / 4;
		//this.camera.rotation.x = -Math.PI / 6;
		this.cameraScreenSpace.lookAt(new THREE.Vector3(0, 0, 0), new THREE.Vector3(0, 0, -1), new THREE.Vector3(0, 1, 0));
		
		this.directionalLight = new THREE.DirectionalLight( 0xffffff, 0.5 );
		this.directionalLight.position.set( 10, 10, 10 );
		this.directionalLight.lookAt( new THREE.Vector3(0, 0, 0));
		this.scenePointCloud.add( this.directionalLight );
		
		let light = new THREE.AmbientLight( 0x555555 ); // soft white light
		this.scenePointCloud.add( light );

		{ // background
			let texture = Utils.createBackgroundTexture(512, 512);

			texture.minFilter = texture.magFilter = THREE.NearestFilter;
			texture.minFilter = texture.magFilter = THREE.LinearFilter;
			let bg = new THREE.Mesh(
				new THREE.PlaneGeometry(2, 2, 1),
				new THREE.MeshBasicMaterial({
					map: texture
				})
			);
			bg.material.depthTest = false;
			bg.material.depthWrite = false;
			this.sceneBG.add(bg);
		}

		// { // lights
		// 	{
		// 		let light = new THREE.DirectionalLight(0xffffff);
		// 		light.position.set(10, 10, 1);
		// 		light.target.position.set(0, 0, 0);
		// 		this.scene.add(light);
		// 	}

		// 	{
		// 		let light = new THREE.DirectionalLight(0xffffff);
		// 		light.position.set(-10, 10, 1);
		// 		light.target.position.set(0, 0, 0);
		// 		this.scene.add(light);
		// 	}

		// 	{
		// 		let light = new THREE.DirectionalLight(0xffffff);
		// 		light.position.set(0, -10, 20);
		// 		light.target.position.set(0, 0, 0);
		// 		this.scene.add(light);
		// 	}
		// }
	}
    /**
     * 向场景添加注释。
     * @param {THREE.Vector3|Array} position - 注释的位置。
     * @param {Object} args - 注释的参数。
     * @returns {Annotation} 创建的注释对象。
     */	
	addAnnotation(position, args = {}){		
		if(position instanceof Array){
			args.position = new THREE.Vector3().fromArray(position);
		} else if (position.x != null) {
			args.position = position;
		}
		let annotation = new Annotation(args);
		this.annotations.add(annotation);

		return annotation;
	}
    /**
     * 获取场景中的所有注释。
     * @returns {Annotation} 注释对象。
     */
	getAnnotations () {
		return this.annotations;
	};
    /**
     * 从场景中移除指定的注释。
     * @param {Annotation} annotationToRemove - 要移除的注释。
     */
	removeAnnotation(annotationToRemove) {
		this.annotations.remove(annotationToRemove);
	}
};
